import { Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';

import defaultLogo from '@/assets/imgs/defaultlogo.png';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useDisableScroll } from '@/hooks/useDisableScroll';
import { useOrganizations } from '@/hooks/useOrganizations';
import { ROUTES } from '@/routes';
import { useTheme } from '@/theme';

import LogoutButton from './shared/LogoutButton';
import SettingsButton from './shared/SettingsButton';

interface NavbarProps {
  className?: string;
}

function Navbar({ className }: NavbarProps) {
  const theme = useTheme();
  const { organizations, isLoading } = useOrganizations();
  const { organizationId, setOrganizationId } = useOrganizationId();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const { isAdminManager } = useAuth();

  useDisableScroll(isSelectOpen);

  // If the stored organizationId is no longer in the list (e.g. deactivated), reset to 0
  useEffect(() => {
    if (!isLoading && organizations && organizationId !== 0) {
      const exists = organizations.some((org) => org.id === organizationId);
      if (!exists) {
        setOrganizationId(0);
      }
    }
  }, [isLoading, organizations, organizationId, setOrganizationId]);

  // Ensure value is always a number to prevent controlled/uncontrolled warning
  // MUI Select requires a consistent value type (number) from the first render
  const selectValue = useMemo(() => {
    return typeof organizationId === 'number' ? organizationId : 0;
  }, [organizationId]);

  // Find the selected organization object based on the ID
  const selectedOrganization = organizations?.find((organization) => organization.id === organizationId);

  return (
    <nav className={className}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {selectedOrganization && selectedOrganization.logoUrl ? (
          <Box component='img' src={selectedOrganization.logoUrl} alt='Hub Nexus' sx={{ maxHeight: '5.75rem', width: '9.25rem' }} />
        ) : (
          <Box component='img' src={defaultLogo} alt='Hub Nexus' sx={{ maxHeight: '5.75rem', width: '9.25rem' }} />
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <FormControl sx={{ minWidth: 220 }}>
            <Typography
              component='span'
              sx={{
                fontSize: 12,
                textTransform: 'uppercase',
                lineHeight: '16px',
                fontWeight: 400,
                color: theme.hhglobal.purpose.textWeak,
                paddingBottom: '4px',
              }}
            >
              Select a client
            </Typography>
            {isLoading ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Skeleton sx={{ marginTop: '3px', bgcolor: theme.hhglobal.purpose.fillWeak }} variant='rounded' width={220} height={36} />
                </Box>
              </Box>
            ) : (
              <>
                <Select
                  value={selectValue}
                  size='small'
                  displayEmpty
                  labelId='select-organization-label'
                  data-testid='select-organization-label'
                  MenuProps={{ disableScrollLock: true }}
                  open={isSelectOpen}
                  onOpen={() => setIsSelectOpen(true)}
                  onClose={() => setIsSelectOpen(false)}
                  onChange={(event) => {
                    setOrganizationId(Number(event.target.value));
                    setIsSelectOpen(false);
                  }}
                  inputProps={{
                    id: 'select-organization-label',
                  }}
                  variant='outlined'
                  sx={{
                    height: '36px',
                    fontSize: theme.hhglobal.typography.body.large.fontSize,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.hhglobal.purpose.strokeStrong,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.hhglobal.purpose.strokeStrong,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.hhglobal.purpose.strokeStrong,
                      borderWidth: '1px',
                    },
                  }}
                >
                  <MenuItem sx={{ fontSize: theme.hhglobal.typography.body.large.fontSize }} value={0}>
                    <em>Select</em>
                  </MenuItem>
                  {organizations?.map((organization) => (
                    <MenuItem
                      key={organization.id}
                      value={organization.id}
                      sx={{ fontSize: theme.hhglobal.typography.body.large.fontSize }}
                    >
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
          </FormControl>
          {isAdminManager && (
            <SettingsButton targetRoute={ROUTES.ADMIN_ORGANIZATION} ariaLabel='Admin Settings' dataTestId='admin-settings-button' />
          )}
          <LogoutButton />
        </Box>
      </Box>
    </nav>
  );
}

export default Navbar;
